/**
 * useGridRows — the ONE read-only grid state hook. `mode` is the only axis.
 *
 *   useServerEnhancedGridState 120 ─┐
 *                                   ├──► useGridRows({ mode })
 *   useClientEnhancedGridState 231 ─┘
 *
 *   SHARED (both modes, verbatim)   gridFilters · searchText · quickFilterOption
 *                                   selectedRows · setSearch · forceRefetch
 *                                   setQuickFilterOption · updateFilters
 *                                   updateSelectedRows · resetToDefault
 *
 *   ┌ mode="local" ─────────────────────────────────────────────────┐
 *   │ applyFiltersAndSearch  filters IN THE BROWSER over every row  │
 *   │ stripEmptyFilters      on hydration                           │
 *   └───────────────────────────────────────────────────────────────┘
 *   ┌ mode="paged" ─────────────────────────────────────────────────┐
 *   │ first · after · sortItems · gridUpdate   cursor window        │
 *   │ quick-filter re-sync effect              re-matches by label  │
 *   └───────────────────────────────────────────────────────────────┘
 *
 * WHY the forks are guarded, never merged: each one fails PLAUSIBLY, not
 * loudly. `applyFiltersAndSearch` in paged mode filters only the rows on the
 * visible page and returns a wrong answer that looks right. The re-sync effect
 * in local mode churns state for a grid that has no pages. Both guards throw
 * or no-op on the wrong mode; `useGridRows.test.ts` proves each one.
 *
 * `mode` is fixed per hook instance — FORK ⓪ throws on a switch rather than
 * merging two shapes into one object. See `assertModeUnchanged`.
 */
import type {
  DataGridPremiumProps,
  GridColDef,
  GridSortModel,
  GridValidRowModel,
  GridValueGetter,
} from '@mui/x-data-grid-premium';
import { isEqual } from 'lodash';
import { KeyOption, matchSorter } from 'match-sorter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import fuzzyComplete from 'src/utils/fuzzyComplete';
import { useDebounce } from 'usehooks-ts';
import type { BaseGridState, BaseGridStateWithHelpers } from '../shared/baseGridTypes';
import { stripEmptyFilters } from '../shared/filterSanitize';
import { dropNonFiniteFilterConditions, dropPersistedNonFiniteFilterConditions } from '../shared/finiteFilterValues';
import type { ServerGridFilter } from '../shared/gridFilterTypes';
import type { GridToolbarQuickFilter } from '../toolbar/GridToolbarV2';

export type GridRowsMode = 'local' | 'paged';

/** Page size used when no persisted `pagination.paginationModel.pageSize` is found. */
const DEFAULT_PAGE_SIZE = 25;

/** Keeps the grid from re-querying (paged) / re-filtering (local) on every keystroke. */
const STATE_DEBOUNCE_MS = 250;

const NO_SEARCH_KEYS: ReadonlyArray<KeyOption<GridValidRowModel>> = [];

export const LOCAL_ONLY_FILTER_ERROR =
  'useGridRows: applyFiltersAndSearch is mode="local" only. In mode="paged" it would filter ' +
  'only the rows of the visible page and return a wrong result that looks correct.';

export const PAGED_ONLY_GRID_UPDATE_ERROR =
  'useGridRows: gridUpdate is mode="paged" only. A local grid has no cursor window to move.';

export const MODE_IS_FIXED_ERROR =
  'useGridRows: `mode` is fixed for the life of a hook instance and changed between renders. ' +
  'Mount a separate grid per mode instead of switching one.';

export function assertLocalMode(mode: GridRowsMode): void {
  if (mode !== 'local') throw new Error(LOCAL_ONLY_FILTER_ERROR);
}

/** FORK ② tripwire — the cursor window only exists in paged mode. */
export function assertPagedMode(mode: GridRowsMode): void {
  if (mode !== 'paged') throw new Error(PAGED_ONLY_GRID_UPDATE_ERROR);
}

/**
 * FORK ⓪ tripwire — `mode` is fixed per hook instance, and a switch is made
 * unrepresentable rather than handled.
 *
 *   render n    mode="paged"  ─┐
 *                              ├─ ONE useState, ONE stableRef, ONE hydration
 *   render n+1  mode="local"  ─┘        ──╳──▶  throw
 *
 * Handling the switch is not enough. `stableRef` is mutated with Object.assign,
 * which copies keys and never deletes them, so the flipped state would carry
 * BOTH forks' properties. Deleting the stale keys would fix the shape and leave
 * the worse half: `cachedState` seeds `useState` ONCE, under the first render's
 * mode, and FORK ③ makes those seeds differ — a grid flipped to "paged" would
 * query the server with locally-stripped filters and return a wrong answer that
 * looks right. Every call site passes a `mode` literal, so this never fires.
 */
export function assertModeUnchanged(previous: GridRowsMode, next: GridRowsMode): void {
  if (previous !== next) throw new Error(MODE_IS_FIXED_ERROR);
}

/**
 * Resolves the filterable value of a column whose cell is neither a row field nor a
 * `valueGetter` (e.g. a value computed inside `renderCell`). Takes precedence over the
 * column's own `valueGetter`. Local mode only — the client-side filter engine is its sole consumer.
 */
export type ValueResolvers<Row extends GridValidRowModel = GridValidRowModel> = Record<string, (row: Row) => unknown>;

/** The part of a column definition the local filter reads a cell through. */
type FilterValueColumn<Row extends GridValidRowModel = GridValidRowModel> = Pick<
  Omit<GridColDef<Row>, 'field'>,
  'valueGetter' | 'valueFormatter'
>;

/**
 * What `applyFiltersAndSearch` reads cells through: the grid's own `columns` (and its
 * `apiRef`, which MUI hands every getter), so a filter compares the value the cell shows.
 */
export type LocalFilterSource<Row extends GridValidRowModel = GridValidRowModel> = {
  columns?: Readonly<Record<string, FilterValueColumn<Row>>>;
  apiRef?: DataGridPremiumProps<Row>['apiRef'];
};

type SharedGridRowsOptions = {
  storageKey: string;
  quickFilterOptions?: GridToolbarQuickFilter[];
  initialState?: BaseGridState['initialState'] | null;
};

export type LocalGridRowsOptions<Row extends GridValidRowModel = GridValidRowModel> = SharedGridRowsOptions & {
  mode: 'local';
  searchKeys: ReadonlyArray<KeyOption<Row>>;
  valueResolvers?: ValueResolvers<Row>;
  getTreeDataPath?: (row: Row) => string[];
};

export type PagedGridRowsOptions = SharedGridRowsOptions & { mode: 'paged' };

export type GridRowsOptions<Row extends GridValidRowModel = GridValidRowModel> =
  | LocalGridRowsOptions<Row>
  | PagedGridRowsOptions;

export type LocalGridRowsState = BaseGridStateWithHelpers & {
  mode: 'local';
  applyFiltersAndSearch: <Row extends GridValidRowModel>(
    data: readonly Row[],
    source?: LocalFilterSource<Row>
  ) => Row[];
};

/**
 * The cursor window a paged grid queries with — data only, no helpers. This is the
 * shape `useServerEnhancedGridQuery` and every domain `use*Page` hook consumes.
 */
export type PagedGridQueryState = BaseGridState & { first: number; after?: string; sortItems: GridSortModel };

export type PagedGridRowsState = BaseGridStateWithHelpers &
  PagedGridQueryState & {
    mode: 'paged';
    gridUpdate: (sortItems: GridSortModel, first: number, after?: string) => void;
  };

export type GridRowsState = LocalGridRowsState | PagedGridRowsState;

/**
 * `first`/`after`/`sortItems` are carried in both modes so the hook keeps ONE
 * state shape (and one `useState`), but they are exposed only by `mode="paged"`
 * and only `gridUpdate` — itself paged-guarded — ever writes them.
 */
type InternalGridRowsState = PagedGridQueryState;

type PersistedGridState = BaseGridState['initialState'] | null;

/** The column state a grid persisted under `storageKey`, or `initialState` when it persisted none. */
function readPersistedGridState(
  storageKey: string,
  initialState: PersistedGridState,
  mode: GridRowsMode
): PersistedGridState {
  const stateJSON = localStorage.getItem(`${storageKey}-column-state`);
  if (!stateJSON) return initialState;
  const state = JSON.parse(stateJSON);
  const { serverFilters, quickFilter, pagination } = state;

  // Sanitised HERE too: a blob persisted before `updateFilters` guarded the live path
  // carries Infinity/NaN as `null`, which the API reads as a real constraint on `null`.
  const persistedFilters = dropPersistedNonFiniteFilterConditions(serverFilters);

  // FORK ③ — local hydration drops filters that carry no value; paged hydration
  // does NOT, and sends them to the server exactly as persisted. Unifying either
  // way changes what one mode requests. See the merge report.
  const gridFilters = mode === 'local' ? stripEmptyFilters(persistedFilters) : persistedFilters;

  return { gridFilters, quickFilter, pagination };
}

function seedGridRowsState(
  cachedState: PersistedGridState,
  quickFilterOptions: GridToolbarQuickFilter[] | undefined
): InternalGridRowsState {
  return {
    first: cachedState?.pagination?.paginationModel?.pageSize ?? DEFAULT_PAGE_SIZE,
    after: undefined,
    sortItems: [],
    gridFilters: cachedState?.gridFilters || [],
    searchText: undefined,
    quickFilterOption:
      (cachedState?.quickFilter && quickFilterOptions?.find((o) => o.label === cachedState.quickFilter)) ||
      quickFilterOptions?.[0],
    selectedRows: {},
  };
}

/** The `StandardPageInput` a paged grid sends for its query state and cursor. */
export type PagedGridInput = Readonly<{
  first: number;
  after?: string;
  sortItems: GridSortModel[number][];
  gridFilters: ServerGridFilter[];
  searchText?: string;
  quickFilters?: ServerGridFilter[];
}>;

export function pagedGridInput(
  state: Pick<PagedGridQueryState, 'first' | 'sortItems' | 'gridFilters' | 'searchText' | 'quickFilterOption'>,
  after: string | undefined
): PagedGridInput {
  return {
    first: state.first,
    after,
    sortItems: [...state.sortItems],
    gridFilters: state.gridFilters,
    searchText: state.searchText,
    quickFilters: state.quickFilterOption?.filters,
  };
}

/**
 * The input of a paged grid's first read, seeded from its persisted state exactly as `useGridRows`
 * seeds it (no cursor, no search, no sort), so a route loader can preload that read.
 */
export function initialPagedGridInput(
  storageKey: string,
  quickFilterOptions?: GridToolbarQuickFilter[],
  initialState: PersistedGridState = null
): PagedGridInput {
  const seed = seedGridRowsState(readPersistedGridState(storageKey, initialState, 'paged'), quickFilterOptions);
  return pagedGridInput(seed, undefined);
}

export function useGridRows(options: PagedGridRowsOptions): PagedGridRowsState;
export function useGridRows<Row extends GridValidRowModel>(options: LocalGridRowsOptions<Row>): LocalGridRowsState;
export function useGridRows(options: GridRowsOptions): GridRowsState {
  const { mode, storageKey, quickFilterOptions, initialState = null } = options;

  const modeRef = useRef(mode);
  assertModeUnchanged(modeRef.current, mode);

  const searchKeys = options.mode === 'local' ? options.searchKeys : undefined;
  const valueResolvers = options.mode === 'local' ? options.valueResolvers : undefined;
  const getTreeDataPath = options.mode === 'local' ? options.getTreeDataPath : undefined;

  const cachedState = useMemo(
    () => readPersistedGridState(storageKey, initialState, mode),
    [storageKey, initialState, mode]
  );

  const [gridRowsState, setGridRowsState] = useState<InternalGridRowsState>(() =>
    seedGridRowsState(cachedState, quickFilterOptions)
  );

  const fuzzyFilter = useMemo(
    () => fuzzyComplete(searchKeys ?? NO_SEARCH_KEYS, { threshold: matchSorter.rankings.CONTAINS }),
    [searchKeys]
  );

  const setSearch = useCallback((value?: string) => {
    setGridRowsState((prev) => ({ ...prev, searchText: value }));
  }, []);

  const forceRefetch = useCallback(() => {
    setGridRowsState((prev) => ({ ...prev }));
  }, []);

  const setQuickFilterOption = useCallback((quickFilterOption?: GridToolbarQuickFilter) => {
    setGridRowsState((prev) => ({ ...prev, quickFilterOption }));
  }, []);

  // Sanitised HERE, at the only writer: `gridFilters` is the live request payload, and
  // `JSON.stringify` serialises Infinity/NaN to `null` — a constraint the user never typed.
  const updateFilters = useCallback((filters: ServerGridFilter[]) => {
    setGridRowsState((prev) => ({ ...prev, gridFilters: dropNonFiniteFilterConditions(filters) }));
  }, []);

  const updateSelectedRows = useCallback((selectedRows: Record<string, unknown>) => {
    setGridRowsState((prev) => ({ ...prev, selectedRows }));
  }, []);

  const resetToDefault = useCallback(() => {
    setGridRowsState((prev) => ({
      ...prev,
      gridFilters: [],
      searchText: undefined,
      quickFilterOption: quickFilterOptions?.[0],
    }));
  }, [quickFilterOptions]);

  const gridUpdate = useCallback(
    (sortItems: GridSortModel, first: number, after?: string) => {
      assertPagedMode(mode);
      // The paged grid re-requests with a fresh sort array whenever its pagination effect re-runs; an
      // unchanged window keeps its state, so the query input (and the rows it answers) keep their identity.
      setGridRowsState((prev) =>
        prev.first === first && prev.after === after && isEqual(prev.sortItems, sortItems)
          ? prev
          : { ...prev, sortItems, first, after }
      );
    },
    [mode]
  );

  const quickFilterOptionRef = useRef(gridRowsState.quickFilterOption);
  quickFilterOptionRef.current = gridRowsState.quickFilterOption;

  useEffect(() => {
    if (mode !== 'paged') return;
    const selected = quickFilterOptionRef.current;
    if (!quickFilterOptions || !selected) return;
    const match = quickFilterOptions.find((q) => q.label === selected.label);
    if (match) {
      setQuickFilterOption(match);
    }
  }, [mode, quickFilterOptions, setQuickFilterOption]);

  // FORK ① — local only. Reads the RAW (undebounced) state on purpose: the grid
  // re-filters from the same render that produced the keystroke.
  const applyFiltersAndSearch = useCallback(
    <Row extends GridValidRowModel>(data: readonly Row[], source?: LocalFilterSource<Row>): Row[] => {
      assertLocalMode(mode);

      let filteredData = [...data];

      // Apply quick filter (our custom filter buttons like "Active Users")
      const quickFilters = gridRowsState.quickFilterOption?.filters;
      if (quickFilters) {
        filteredData = filteredData.filter((row) => {
          return quickFilters.every((filter) => {
            return applyServerGridFilter(row, filter, valueResolvers, source);
          });
        });
      }

      // Apply custom filters from toolbar
      if (gridRowsState.gridFilters.length > 0) {
        filteredData = filteredData.filter((row) => {
          return gridRowsState.gridFilters.every((filter) => {
            return applyServerGridFilter(row, filter, valueResolvers, source);
          });
        });
      }

      if (gridRowsState.searchText) {
        filteredData = fuzzyFilter(filteredData, { inputValue: gridRowsState.searchText });

        // For tree data, include parent rows of any matching children
        if (getTreeDataPath) {
          const matchingPaths = new Set<string>();

          // Collect all ancestor paths for matching rows
          filteredData.forEach((row) => {
            const path = getTreeDataPath(row);
            // Add all ancestor paths (e.g., for ["A", "B", "C"], add "A" and "A/B")
            for (let i = 1; i < path.length; i++) {
              matchingPaths.add(path.slice(0, i).join('/'));
            }
          });

          // Add parent rows that aren't already in the filtered results
          if (matchingPaths.size > 0) {
            const filteredIds = new Set(filteredData.map((r) => r.id));
            const parentRows = data.filter((row) => {
              if (filteredIds.has(row.id)) return false;
              const rowPath = getTreeDataPath(row).join('/');
              return matchingPaths.has(rowPath);
            });
            filteredData = [...parentRows, ...filteredData];
          }
        }
      }

      return filteredData;
    },
    [
      mode,
      gridRowsState.quickFilterOption,
      gridRowsState.gridFilters,
      gridRowsState.searchText,
      fuzzyFilter,
      valueResolvers,
      getTreeDataPath,
    ]
  );

  // This prevents excessive re-renders while typing / too many requests
  const debouncedState = useDebounce(gridRowsState, STATE_DEBOUNCE_MS);

  // Use a ref to maintain a stable object reference while updating its properties
  // This prevents unnecessary re-renders in components that depend on this object's identity
  const stableRef = useRef<GridRowsState | null>(null);

  const shared = {
    gridFilters: debouncedState.gridFilters,
    searchText: debouncedState.searchText,
    quickFilterOption: debouncedState.quickFilterOption,
    selectedRows: debouncedState.selectedRows,
    quickFilterOptions,
    setSearch,
    forceRefetch,
    setQuickFilterOption,
    updateFilters,
    updateSelectedRows,
    resetToDefault,
  };

  const next: GridRowsState =
    mode === 'paged'
      ? {
          ...shared,
          mode,
          first: debouncedState.first,
          after: debouncedState.after,
          sortItems: debouncedState.sortItems,
          gridUpdate,
        }
      : { ...shared, mode, applyFiltersAndSearch };

  if (!stableRef.current) {
    stableRef.current = next;
  } else {
    // Update all properties on the existing object to maintain reference stability
    Object.assign(stableRef.current, next);
  }

  return stableRef.current;
}

/** MUI's getter/formatter signature: the cell's raw field value is typed `never` until a column narrows it. */
type CellReadArgs<Row extends GridValidRowModel> = Parameters<GridValueGetter<Row>>;

/**
 * The value a filter on `field` compares, in precedence order:
 *
 *   valueResolvers[field]   the grid says what a renderCell-only cell means
 *   column.valueGetter      the value MUI renders, sorts and exports (cents → dollars, nested name, …)
 *   row[field]              a plain field column
 */
function readCellValue<Row extends GridValidRowModel>(
  row: Row,
  field: string,
  valueResolvers: ValueResolvers | undefined,
  source: LocalFilterSource<Row> | undefined
): unknown {
  const resolver = valueResolvers?.[field];
  if (resolver) return resolver(row);

  const column = source?.columns?.[field];
  if (!column?.valueGetter) return row[field];

  const args = cellReadArgs(row, field, row[field], column, source);
  try {
    return column.valueGetter(...args);
  } catch {
    // A getter that walks the mounted grid (`apiRef.current.getRowNode`) has nothing to read
    // before MUI's first paint; that cell filters as the raw field, as it did before.
    return row[field];
  }
}

/** The text the cell shows when its column formats the value; `null` when it does not. */
function readCellText<Row extends GridValidRowModel>(
  row: Row,
  field: string,
  value: unknown,
  valueResolvers: ValueResolvers | undefined,
  source: LocalFilterSource<Row> | undefined
): string | null {
  // An explicit resolver already returns the comparable value; formatting it again could double-format.
  if (valueResolvers?.[field]) return null;

  const column = source?.columns?.[field];
  if (!column?.valueFormatter) return null;

  try {
    const formatted = column.valueFormatter(...cellReadArgs(row, field, value, column, source));
    return toSearchableText(formatted);
  } catch {
    return null;
  }
}

function cellReadArgs<Row extends GridValidRowModel>(
  row: Row,
  field: string,
  value: unknown,
  column: FilterValueColumn<Row>,
  source: LocalFilterSource<Row> | undefined
): CellReadArgs<Row> {
  // The same four arguments MUI passes; `value` is `never`-typed in MUI's signature.
  return [value as never, row, { ...column, field } as GridColDef<Row>, source?.apiRef as CellReadArgs<Row>[3]];
}

/** A primitive (or list of primitives) as text; an object has no text a user could type. */
function toSearchableText(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) return null;
  return String(value);
}

/** A Date cell compares on its instant; the date filter sends that instant as an ISO string. */
function rangeOperands(fieldValue: unknown, bound: string | number | boolean): [unknown, unknown] {
  if (fieldValue instanceof Date && (typeof bound === 'string' || typeof bound === 'number')) {
    return [fieldValue.getTime(), new Date(bound).getTime()];
  }
  return [fieldValue, bound];
}

/**
 * The comparison ladder is deliberately NOT retyped: a cell value is `any` off MUI's
 * `GridValidRowModel`, and narrowing it would change `>=` / `includes` semantics for Date and
 * NaN operands.
 */
function applyServerGridFilter<Row extends GridValidRowModel>(
  row: Row,
  filter: ServerGridFilter,
  valueResolvers?: ValueResolvers,
  source?: LocalFilterSource<Row>
): boolean {
  // If no where conditions, pass the filter
  if (!filter.where || filter.where.length === 0) return true;

  const fieldValue = readCellValue(row, filter.field, valueResolvers, source);

  // Apply each where condition (AND logic)
  return filter.where.every((condition) => {
    if (condition.contains !== undefined) {
      const term = String(condition.contains).toLowerCase();
      const texts = [toSearchableText(fieldValue), readCellText(row, filter.field, fieldValue, valueResolvers, source)];
      return texts.some((text) => text?.toLowerCase().includes(term));
    }

    if (condition.equals !== undefined) {
      return fieldValue === condition.equals;
    }

    if (condition.in !== undefined) {
      return condition.in.includes(fieldValue as string | number | boolean);
    }

    if (condition.notIn !== undefined) {
      return !condition.notIn.includes(fieldValue as string | number | boolean);
    }

    if (condition.has !== undefined) {
      if (!Array.isArray(fieldValue)) return false;
      return fieldValue.includes(condition.has);
    }

    if (condition.hasSome !== undefined) {
      if (!Array.isArray(fieldValue)) return false;
      return condition.hasSome.some((val) => fieldValue.includes(val));
    }

    if (condition.gte !== undefined) {
      if (fieldValue == null) return false;
      const [value, bound] = rangeOperands(fieldValue, condition.gte);
      return (value as number) >= (bound as number);
    }

    if (condition.lte !== undefined) {
      if (fieldValue == null) return false;
      const [value, bound] = rangeOperands(fieldValue, condition.lte);
      return (value as number) <= (bound as number);
    }

    if (condition.not !== undefined) {
      return fieldValue !== condition.not;
    }

    return true;
  });
}
